/**
 * Ecosystem Gateway — Vigilant Entities public landing (v2).
 * ──────────────────────────────────────────────────────────
 * v1 was too tiny + too cluttered + forced dark. v2 follows the brand
 * book exactly:
 *
 *   • Honours the global theme (light = warm cream + executive black + gold;
 *     dark = executive black + gold). No more hard-coded `bg-[#0A0A0A]`.
 *   • LARGE readable typography. Hero is 5xl→8xl (was 4xl).
 *   • Single, focused hero — not 3 rails of competing content.
 *   • "Exclusive international" feel: serif-grade kerning on the hero,
 *     gold-foil heading, generous whitespace, asymmetric grid.
 *   • Interactive: 4 portal nodes you can hover OR click; the active
 *     portal expands inline with its benefits — no separate modal,
 *     no separate detail card stealing focus from the ring.
 *   • A live animated network sits BEHIND the hero (lighter on light
 *     theme, deeper on dark theme).
 *
 * Routes that funnel here (App.js): `/`, `/home`, `/platform`,
 * `/modules`, `/solutions`, `/industries`.
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Users, Rocket, Building2, GraduationCap, ArrowRight,
  Sparkles, ShieldCheck, Activity, Globe, Bot,
} from 'lucide-react';
import AuditIntegrityPill from '@/components/AuditIntegrityPill';
import { useTheme } from '@/context/ThemeContext';
import PublicNavbar from '@/components/PublicNavbar';
import NetworkCanvas from '@/components/NetworkCanvas';
import SovereigntyBanner from '@/components/SovereigntyBanner';
import PrivacyGlanceWidget from '@/components/PrivacyGlanceWidget';
import SovereigntyStreamTicker from '@/components/SovereigntyStreamTicker';
import VersionBadge from '@/components/VersionBadge';
import { BrochureLangMenu } from '@/components/BrochureLangMenu';

const PORTALS = [
  {
    id: 'investor',
    label: 'Investor',
    icon: Users,
    accent: '#AE52EA',
    blurb: 'Discover vetted ventures, AI-matched to your thesis. Track every deal from origination to exit.',
    benefits: ['Deal Flow', 'Venture Intelligence', 'Due Diligence', 'Founder Matching', 'Portfolio Visibility'],
  },
  {
    id: 'startup',
    label: 'Startup',
    icon: Rocket,
    accent: '#7C3AED',
    blurb: 'Founders-first OS. Fundraising, growth, governance — and Iqra AI at your side.',
    benefits: ['Fundraising', 'Investor Access', 'Growth Tracking', 'AI Advisory', 'Strategic Partnerships'],
  },
  {
    id: 'holding',
    label: 'Holding Company',
    icon: Building2,
    accent: '#3B82F6',
    blurb: 'Govern every subsidiary, every board, every entity — one cockpit, sovereign control.',
    benefits: ['Entity Governance', 'Board Management', 'Risk & Compliance', 'Group Analytics', 'Subsidiary Mapping'],
  },
  {
    id: 'incubator',
    label: 'Incubator',
    icon: GraduationCap,
    accent: '#10B981',
    blurb: 'Run cohorts at sovereign scale. Map mentors, surface intros, measure outcomes.',
    benefits: ['Cohort Management', 'Startup Monitoring', 'KPI Tracking', 'Mentor Network', 'Investor Introductions'],
  },
];

// ─────────────────────────── Network canvas ───────────────────────────
// v162 — extracted to `components/NetworkCanvas.jsx` so the Login page
// uses the IDENTICAL canvas component. Same particle palette, same
// orbital rings, same theme adaptation — splash → /login feels like one
// continuous canvas instead of two different visualisations.

// ─────────────────────────── Portal card ───────────────────────────
function PortalCard({ portal, isActive, isDark, onActivate }) {
  const Icon = portal.icon;
  return (
    <button
      onMouseEnter={onActivate}
      onFocus={onActivate}
      onClick={() => window.location.assign(`/pricing?persona=${portal.id === 'incubator' ? 'incubator' : portal.id === 'holding' ? 'holding' : portal.id === 'investor' ? 'investor' : 'startup'}`)}
      data-testid={`gateway-portal-${portal.id}`}
      className={`relative text-left p-7 rounded-2xl border transition-all duration-500 group focus:outline-none ${
        isActive
          ? 'scale-[1.015]'
          : 'hover:scale-[1.01] hover:-translate-y-0.5'
      }`}
      style={{
        background: isDark
          ? (isActive
              ? 'linear-gradient(180deg, rgba(255,255,255,0.06) 0%, rgba(20,20,20,0.92) 100%)'
              : 'linear-gradient(180deg, rgba(255,255,255,0.02) 0%, rgba(10,10,10,0.85) 100%)')
          : (isActive
              ? 'linear-gradient(180deg, #FFFFFF 0%, #F7F5FF 100%)'
              : 'linear-gradient(180deg, rgba(255,255,255,0.85) 0%, rgba(247,245,255,0.75) 100%)'),
        borderColor: isActive ? `${portal.accent}80` : (isDark ? 'rgba(174,82,234,0.18)' : 'rgba(174,82,234,0.25)'),
        boxShadow: isActive
          ? `0 30px 80px -30px ${portal.accent}66, inset 0 1px 0 ${isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.02)'}`
          : (isDark ? '0 6px 22px -10px rgba(0,0,0,0.5)' : '0 8px 20px -12px rgba(94,31,139,0.15)'),
        backdropFilter: 'blur(14px)',
      }}
    >
      <div
        className="h-14 w-14 rounded-xl flex items-center justify-center mb-5 transition-transform group-hover:scale-110 border"
        style={{ backgroundColor: `${portal.accent}22`, color: portal.accent, borderColor: `${portal.accent}50` }}
      >
        <Icon size={26} strokeWidth={1.5} />
      </div>
      <div className="ve-eyebrow" style={{ color: portal.accent }}>{portal.label}</div>
      <h3 className={`display text-2xl md:text-3xl mt-2 leading-tight ${isDark ? 'text-white' : 'text-slate-900'}`}>
        Enter as {portal.label}
      </h3>
      <p className={`text-sm md:text-[15px] mt-3 leading-relaxed ${isDark ? 'text-white/70' : 'text-slate-600'}`}>
        {portal.blurb}
      </p>
      <div className="mt-5 flex flex-wrap gap-1.5">
        {portal.benefits.slice(0, isActive ? 5 : 3).map((b) => (
          <span
            key={b}
            className="text-[11px] uppercase tracking-[0.15em] px-2.5 py-1 rounded-full border"
            style={{ borderColor: `${portal.accent}55`, color: portal.accent, background: `${portal.accent}10` }}
          >
            {b}
          </span>
        ))}
      </div>
      <div className="mt-6 inline-flex items-center gap-1.5 text-sm font-bold uppercase tracking-[0.18em]" style={{ color: portal.accent }}>
        Pick modules <ArrowRight size={14} className="transition-transform group-hover:translate-x-0.5" />
      </div>
    </button>
  );
}

// ─────────────────────────── Main ───────────────────────────
export default function EcosystemGateway() {
  // First paint shows a neutral overview ("the ecosystem"); the active
  // persona then auto-rotates every 5s so the page no longer reads as
  // an "investor-first" portal. Hovering any portal card pauses the
  // rotation and locks in the user's choice.
  const [active, setActive] = useState(null);
  const [paused, setPaused] = useState(false);
  const { effective } = useTheme();
  const isDark = effective === 'dark';

  // Auto-rotate through the four portals every 5s when nothing is
  // hovered. We rotate through `null → investor → startup → holding →
  // incubator → null …` so the neutral "ecosystem" framing reappears
  // each cycle.
  useEffect(() => {
    if (paused) return undefined;
    const order = [null, 'investor', 'startup', 'holding', 'incubator'];
    const t = setInterval(() => {
      setActive((cur) => {
        const idx = order.indexOf(cur);
        return order[(idx + 1) % order.length];
      });
    }, 5000);
    return () => clearInterval(t);
  }, [paused]);

  const activePortal = useMemo(() => PORTALS.find((p) => p.id === active), [active]);

  return (
    <div
      className={`relative min-h-screen ${isDark ? 'text-white' : 'text-slate-900'}`}
      data-testid="ecosystem-gateway"
      style={{
        background: isDark
          ? // Sovereign Violet night canvas — three layered radial plumes
            // for executive depth on pure black.
            'radial-gradient(1300px 700px at 85% -10%, rgba(174,82,234,0.18), transparent 60%),'
          + ' radial-gradient(1000px 700px at -10% 110%, rgba(94,31,139,0.16), transparent 60%),'
          + ' radial-gradient(900px 900px at 50% 50%, rgba(121,50,174,0.08), transparent 70%),'
          + ' #000000'
          : // Day canvas — soft cream with violet plumes, mirrors the
            // night composition so the visual identity stays intact when
            // the user flips to light mode.
            'radial-gradient(1300px 700px at 85% -10%, rgba(174,82,234,0.10), transparent 60%),'
          + ' radial-gradient(1000px 700px at -10% 110%, rgba(94,31,139,0.08), transparent 60%),'
          + ' radial-gradient(900px 900px at 50% 50%, rgba(121,50,174,0.05), transparent 70%),'
          + ' #FAFAF7',
      }}
    >
      {/* v160 — Live canvas renders in BOTH themes.
          v162 — Now uses the shared <NetworkCanvas /> from
          /components/NetworkCanvas.jsx so the Login page renders the
          EXACT same canvas — splash → /login feels seamless. */}
      <NetworkCanvas isDark={isDark} testId="gateway-network-canvas" />

      {/* Unified public navigation — same on every public surface */}
      <PublicNavbar variant={isDark ? 'dark' : 'auto'} />

      {/* Live Sovereignty Stream — Bloomberg-style anonymised events */}
      <div className="relative z-10 ve-container pt-4">
        <SovereigntyStreamTicker />
      </div>

      {/* ── HERO ── */}
      <section className="relative z-10 ve-container pt-10 md:pt-20 pb-16">
        <div className="grid lg:grid-cols-12 gap-10 items-start">
          <div className="lg:col-span-7">
            <div className="ve-eyebrow flex items-center gap-2 flex-wrap" style={{ color: 'var(--ve-violet-bright)' }}>
              <span className="h-1.5 w-1.5 rounded-full bg-emerald-500 animate-pulse" />
              Live · Sovereign · Interconnected
              <VersionBadge className="ml-1" />
            </div>
            <h1 className={`display mt-4 text-[2.5rem] leading-[1.05] sm:text-6xl lg:text-7xl xl:text-8xl sm:leading-[0.98] tracking-tight ${isDark ? 'text-white' : 'text-slate-900'}`}>
              One ecosystem.<br />
              <span className="ve-violet-ink">Four gateways.</span>
            </h1>
            <p className={`mt-7 max-w-xl text-base sm:text-lg md:text-xl leading-relaxed ${isDark ? 'text-white/70' : 'text-slate-700'}`}>
              Amwāl OS is the operating system for sovereign ventures —
              connecting capital, innovation, governance, and growth into one
              living network. <span className="font-semibold">For investors, founders, holdings, and incubators alike.</span>
            </p>
            <div className="mt-9 flex flex-wrap items-center gap-3 sm:gap-4">
              <Link to="/pricing" data-testid="home-hero-create-cta" className="ve-btn-primary text-sm py-3 px-5 sm:py-3.5 sm:px-7">
                Pick your modules <ArrowRight size={16} />
              </Link>
              <Link to="/login" data-testid="home-hero-signin-cta" className="text-sm py-3 px-5 sm:py-3.5 sm:px-7 rounded font-bold uppercase tracking-[0.18em] border-2 border-[var(--ve-violet-bright)] text-[var(--ve-violet-bright)] hover:bg-[var(--ve-violet)]/15 transition-colors inline-flex items-center gap-2">
                Login
              </Link>
              <BrochureLangMenu
                testId="hero-download-brochure"
                label="Sales Brochure"
                className={`text-sm py-3 px-5 sm:py-3.5 sm:px-7 rounded font-bold uppercase tracking-[0.18em] border-2 transition-colors inline-flex items-center gap-2 ${
                  isDark
                    ? 'border-white/25 text-white/85 hover:bg-white/10'
                    : 'border-slate-300 text-slate-700 hover:bg-slate-100'
                }`}
              />
            </div>

            {/* Sovereign-AI trust ribbon — links to /security */}
            <SovereigntyBanner className="mt-7" />

            {/* Trust strip */}
            <div className="mt-10 grid grid-cols-2 sm:grid-cols-4 gap-4 sm:gap-6">
              <Stat value="2,543" label="Entities" isDark={isDark} />
              <Stat value="18,702" label="Connections" isDark={isDark} />
              <Stat value="$12.8B" label="Deployed capital" isDark={isDark} />
              <Stat value="42" label="Countries" isDark={isDark} />
            </div>
          </div>

          {/* Active portal preview card */}
          <div
            className="lg:col-span-5 lg:pl-6"
            onMouseEnter={() => setPaused(true)}
            onMouseLeave={() => setPaused(false)}
          >
            <PreviewCard activePortal={activePortal} isDark={isDark} />
          </div>
        </div>
      </section>

      {/* ── 4 PORTALS GRID ── */}
      <section id="portals" className="relative z-10 ve-container py-10 md:py-16">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between mb-8 md:mb-10 gap-4">
          <div>
            <div className="ve-eyebrow" style={{ color: 'var(--ve-violet-bright)' }}>The Four Gateways</div>
            <h2 className={`display text-3xl sm:text-4xl md:text-5xl mt-2 leading-tight ${isDark ? 'text-white' : 'text-slate-900'}`}>
              Choose your way in.
            </h2>
          </div>
          <p className={`max-w-md text-sm sm:text-base ${isDark ? 'text-white/65' : 'text-slate-600'}`}>
            Every login leads to a tailored cockpit. Hover any tile to preview, or click to enter.
          </p>
        </div>
        <div
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-5"
          onMouseEnter={() => setPaused(true)}
          onMouseLeave={() => setPaused(false)}
        >
          {PORTALS.map((p) => (
            <PortalCard
              key={p.id}
              portal={p}
              isActive={active === p.id}
              isDark={isDark}
              onActivate={() => setActive(p.id)}
            />
          ))}
        </div>
      </section>

      {/* ── 26 AI AGENTS — SOVEREIGN COLLECTIVE STRIP ── */}
      <section
        className="relative z-10 ve-container py-12 md:py-16"
        data-testid="gateway-ai-collective"
      >
        <div
          className="relative overflow-hidden rounded-2xl border p-5 sm:p-8 md:p-12"
          style={{
            background: isDark
              ? 'linear-gradient(135deg, rgba(20,20,20,0.92) 0%, rgba(40,20,60,0.65) 50%, rgba(10,10,10,0.92) 100%)'
              : 'linear-gradient(135deg, rgba(247,245,255,0.96) 0%, rgba(232,220,255,0.65) 50%, rgba(255,251,235,0.92) 100%)',
            borderColor: isDark ? 'rgba(174,82,234,0.32)' : 'rgba(174,82,234,0.22)',
            boxShadow: isDark
              ? '0 40px 100px -40px rgba(174,82,234,0.40), inset 0 1px 0 rgba(255,255,255,0.04)'
              : '0 24px 60px -30px rgba(94,31,139,0.18)',
          }}
        >
          {/* Soft glow accents */}
          <div
            className="absolute inset-0 -z-10 opacity-50 pointer-events-none"
            style={{
              background:
                'radial-gradient(circle at 12% 50%, rgba(174,82,234,0.25) 0%, transparent 45%), radial-gradient(circle at 88% 30%, rgba(212,175,55,0.18) 0%, transparent 40%)',
            }}
          />
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
            <div className="lg:col-span-7">
              <div className="flex items-center gap-2">
                <Bot size={14} style={{ color: '#D4AF37' }} />
                <span
                  className="text-[10px] uppercase tracking-[0.32em] font-bold"
                  style={{ color: '#D4AF37' }}
                >
                  [SOVEREIGN AI · COLLECTIVE]
                </span>
              </div>
              <h2
                className={`display text-2xl sm:text-3xl md:text-5xl mt-4 leading-tight ${
                  isDark ? 'text-white' : 'text-slate-900'
                }`}
              >
                <span className="italic" style={{ color: 'var(--ve-violet-bright)' }}>
                  26 AI agents.
                </span>{' '}
                One council.{' '}
                <br className="hidden md:block" />
                Every decision reviewed.
              </h2>
              <p
                className={`text-sm sm:text-base md:text-lg mt-5 max-w-2xl leading-relaxed ${
                  isDark ? 'text-white/70' : 'text-slate-700'
                }`}
              >
                A council of specialised agents reasons across every
                governance domain — risk, audit, finance, compliance, ESG —
                in real time. Runs entirely inside your boundary. Zero data
                leaves.
              </p>
              <div
                className={`mt-6 flex flex-wrap items-center gap-x-3 gap-y-2 text-[11px] uppercase tracking-[0.22em] font-bold ${
                  isDark ? 'text-white/55' : 'text-slate-600'
                }`}
              >
                <span>Specialised personas across every governance domain</span>
              </div>
            </div>

            <div className="lg:col-span-5">
              <div className="grid grid-cols-3 gap-2 sm:gap-3">
                {[
                  { k: '26', v: 'AI personas', testid: 'ai-stat-personas' },
                  { k: '0',  v: 'Data leaves', testid: 'ai-stat-data-leaves' },
                  { k: '24/7', v: 'Live council', testid: 'ai-stat-uptime' },
                ].map((s) => (
                  <div
                    key={s.v}
                    data-testid={s.testid}
                    className={`rounded-md border px-2 sm:px-3 py-3 sm:py-4 backdrop-blur ${
                      isDark
                        ? 'border-white/10 bg-white/[0.03]'
                        : 'border-slate-200/80 bg-white/70'
                    }`}
                  >
                    <div
                      className="display text-xl sm:text-2xl md:text-3xl"
                      style={{ color: '#D4AF37' }}
                    >
                      {s.k}
                    </div>
                    <div
                      className={`text-[9px] uppercase tracking-[0.18em] mt-1 sm:mt-1.5 font-bold ${
                        isDark ? 'text-white/55' : 'text-slate-500'
                      }`}
                    >
                      {s.v}
                    </div>
                  </div>
                ))}
              </div>
              <Link
                to="/collective"
                data-testid="cta-meet-the-collective"
                className={`mt-5 inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] font-bold border-b pb-1 transition-colors ${
                  isDark
                    ? 'text-white/80 hover:text-white border-white/20 hover:border-[#D4AF37]'
                    : 'text-slate-700 hover:text-slate-900 border-slate-300 hover:border-[var(--ve-violet)]'
                }`}
              >
                Meet the collective <ArrowRight size={12} />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* ── CAPABILITIES ── */}
      <section id="capabilities" className="relative z-10 ve-container py-16 md:py-24">
        <div className="grid lg:grid-cols-12 gap-10 items-start">
          <div className="lg:col-span-5">
            <div className="ve-eyebrow" style={{ color: 'var(--ve-violet-bright)' }}>Why Amwāl</div>
            <h2 className={`display text-3xl sm:text-4xl lg:text-5xl mt-2 leading-tight ${isDark ? 'text-white' : 'text-slate-900'}`}>
              Sovereign-grade.<br /> Built for scale.
            </h2>
            <p className={`mt-6 text-base sm:text-lg leading-relaxed ${isDark ? 'text-white/65' : 'text-slate-700'}`}>
              From sovereign wealth funds to first-time founders — every gateway
              shares one truth: the underlying graph. <span className="font-bold ve-violet-ink">Shura Nexus</span>™
              connects every entity, every relationship, every signal in real time.
            </p>
            <Link to="/pricing" className="mt-7 inline-flex items-center gap-2 text-sm font-bold uppercase tracking-[0.18em] text-[var(--ve-violet-bright)] hover:text-[var(--ve-violet-glow)] transition-colors">
              See pricing <ArrowRight size={14} />
            </Link>
          </div>
          <div className="lg:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Capability icon={Sparkles}    title="AI-Powered Intelligence" copy="Real-time insights, predictive analytics, hidden-relationship discovery." isDark={isDark} />
            <Capability icon={ShieldCheck} title="Sovereign Governance"    copy="Enterprise-grade security, audit-by-default, granular RBAC." isDark={isDark} />
            <Capability icon={Activity}    title="Living Network Graph"    copy="Every action updates Shura Nexus™ — your ecosystem moves with you." isDark={isDark} />
            <Capability icon={Globe}       title="Multi-Party Collaboration" copy="Investor + Founder + University + Government — one room, one Shura." isDark={isDark} />
          </div>

          {/* Public privacy glance — anonymised platform-wide, refreshes hourly */}
          <div className="mt-10 sm:mt-14 max-w-5xl mx-auto col-span-1 lg:col-span-12 w-full">
            <PrivacyGlanceWidget />
            <div className={`mt-3 text-center text-[10px] uppercase tracking-[0.18em] ${isDark ? 'text-white/45' : 'text-slate-500'}`}>
              Anonymised, aggregated across all tenants. Verifies the sovereignty promise above isn&apos;t just words.
            </div>
          </div>
        </div>
      </section>

      {/* ── FOOTER ── */}
      <footer className={`relative z-10 border-t mt-10 ${isDark ? 'border-white/10' : 'border-slate-200'}`}>
        <div className="ve-container py-8 sm:py-10 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div className="flex items-center gap-3 sm:gap-4 flex-wrap">
            <div className={`text-[10px] uppercase tracking-[0.25em] ${isDark ? 'text-white/45' : 'text-slate-500'}`}>
              © 2026 Amwāl OS · Vigilant Entities · v1.6.4 · All rights reserved
            </div>
            <AuditIntegrityPill />
          </div>
          <div className={`flex items-center gap-3 sm:gap-6 flex-wrap text-[10px] sm:text-[11px] uppercase tracking-[0.18em] font-bold ${isDark ? 'text-white/65' : 'text-slate-600'}`}>
            <Link to="/modules-full" className="hover:text-[var(--ve-violet)] dark:hover:text-[var(--ve-violet-bright)] transition-colors">Modules</Link>
            <Link to="/security" className="hover:text-[var(--ve-violet)] dark:hover:text-[var(--ve-violet-bright)] transition-colors">Security</Link>
            <Link to="/trust" className="hover:text-[var(--ve-violet)] dark:hover:text-[var(--ve-violet-bright)] transition-colors">Trust</Link>
            <Link to="/pricing" className="hover:text-[var(--ve-violet)] dark:hover:text-[var(--ve-violet-bright)] transition-colors">Pricing</Link>
            <Link to="/login" className="hover:text-[var(--ve-violet)] dark:hover:text-[var(--ve-violet-bright)] transition-colors">Sign In</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}

// Neutral "ecosystem" card shown when no portal is active (first paint
// + every rotation reset) — replaces the old "Built for investors"
// default framing.
function PreviewCard({ activePortal, isDark }) {
  if (!activePortal) {
    return (
      <div
        className="rounded-2xl p-5 sm:p-7 md:p-9 border transition-all duration-500"
        style={{
          background: isDark
            ? 'linear-gradient(180deg, rgba(20,20,20,0.85) 0%, rgba(10,10,10,0.92) 100%)'
            : 'linear-gradient(180deg, rgba(255,255,255,0.96) 0%, rgba(247,245,255,0.92) 100%)',
          borderColor: isDark ? 'rgba(174,82,234,0.32)' : 'rgba(174,82,234,0.25)',
          boxShadow: isDark
            ? '0 40px 100px -40px rgba(174,82,234,0.45), inset 0 1px 0 rgba(255,255,255,0.04)'
            : '0 24px 60px -30px rgba(94,31,139,0.25)',
          backdropFilter: 'blur(20px)',
        }}
        data-testid="gateway-active-portal-card"
      >
        <div className="flex items-start gap-4">
          <div
            className="h-16 w-16 rounded-2xl flex items-center justify-center shrink-0 border"
            style={{ backgroundColor: 'rgba(174,82,234,0.18)', color: '#AE52EA', borderColor: 'rgba(174,82,234,0.40)' }}
          >
            <Globe size={32} strokeWidth={1.5} />
          </div>
          <div>
            <div className="ve-eyebrow" style={{ color: '#AE52EA' }}>The Ecosystem</div>
            <div className={`display text-2xl sm:text-3xl md:text-4xl mt-1 leading-tight ${isDark ? 'text-white' : 'text-slate-900'}`}>
              Built for your network.
            </div>
          </div>
        </div>
        <p className={`text-sm sm:text-base md:text-lg leading-relaxed mt-5 ${isDark ? 'text-white/75' : 'text-slate-700'}`}>
          Four portals. One sovereign graph. Whether you raise capital,
          deploy it, govern it, or grow it — Amwāl OS connects every
          actor in your ecosystem under one transparent operating model.
        </p>
        <div className="mt-5 flex flex-wrap gap-2">
          {['Investors', 'Startups', 'Holdings', 'Incubators'].map((b) => (
            <span
              key={b}
              className="text-[11px] uppercase tracking-[0.15em] font-bold px-3 py-1.5 rounded-full border"
              style={{ borderColor: 'rgba(174,82,234,0.45)', color: '#AE52EA', background: 'rgba(174,82,234,0.12)' }}
            >
              {b}
            </span>
          ))}
        </div>
        <Link
          to="/pricing"
          className="mt-7 inline-flex items-center gap-2 text-sm font-bold uppercase tracking-[0.18em] transition-transform hover:translate-x-0.5"
          style={{ color: '#AE52EA' }}
        >
          Explore modules <ArrowRight size={14} />
        </Link>
      </div>
    );
  }
  return (
    <div
      key={activePortal.id}
      className="rounded-2xl p-5 sm:p-7 md:p-9 border transition-all duration-500"
      style={{
        background: isDark
          ? 'linear-gradient(180deg, rgba(20,20,20,0.85) 0%, rgba(10,10,10,0.92) 100%)'
          : 'linear-gradient(180deg, rgba(255,255,255,0.96) 0%, rgba(247,245,255,0.92) 100%)',
        borderColor: `${activePortal.accent}55`,
        boxShadow: `0 40px 100px -40px ${activePortal.accent}55, inset 0 1px 0 ${isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.02)'}`,
        backdropFilter: 'blur(20px)',
      }}
      data-testid="gateway-active-portal-card"
    >
      <div className="flex items-start gap-4">
        <div
          className="h-14 w-14 sm:h-16 sm:w-16 rounded-2xl flex items-center justify-center shrink-0 border"
          style={{ backgroundColor: `${activePortal.accent}22`, color: activePortal.accent, borderColor: `${activePortal.accent}50` }}
        >
          <activePortal.icon size={28} strokeWidth={1.5} />
        </div>
        <div>
          <div className="ve-eyebrow" style={{ color: activePortal.accent }}>
            {activePortal.label} Portal
          </div>
          <div className={`display text-2xl sm:text-3xl md:text-4xl mt-1 leading-tight ${isDark ? 'text-white' : 'text-slate-900'}`}>
            Built for {activePortal.label.toLowerCase()}s.
          </div>
        </div>
      </div>
      <p className={`text-sm sm:text-base md:text-lg leading-relaxed mt-5 ${isDark ? 'text-white/75' : 'text-slate-700'}`}>
        {activePortal.blurb}
      </p>
      <div className="mt-5 flex flex-wrap gap-2">
        {activePortal.benefits.map((b) => (
          <span
            key={b}
            className="text-[11px] uppercase tracking-[0.15em] font-bold px-3 py-1.5 rounded-full border"
            style={{
              borderColor: `${activePortal.accent}60`,
              color: activePortal.accent,
              background: `${activePortal.accent}1a`,
            }}
          >
            {b}
          </span>
        ))}
      </div>
      <Link
        to={`/pricing?persona=${activePortal.id}`}
        className="mt-7 inline-flex items-center gap-2 text-sm font-bold uppercase tracking-[0.18em] transition-transform hover:translate-x-0.5"
        style={{ color: activePortal.accent }}
      >
        Pick your modules <ArrowRight size={14} />
      </Link>
    </div>
  );
}

function Stat({ value, label, isDark }) {
  return (
    <div>
      <div className="display text-2xl sm:text-3xl md:text-4xl text-[var(--ve-violet-bright)] tabular-nums" style={{ filter: 'drop-shadow(0 0 24px rgba(174,82,234,0.35))' }}>{value}</div>
      <div className={`text-[10px] sm:text-[11px] uppercase tracking-[0.18em] mt-1.5 ${isDark ? 'text-white/55' : 'text-slate-500'}`}>{label}</div>
    </div>
  );
}

function Capability({ icon: Icon, title, copy, isDark }) {
  return (
    <div
      className="group rounded-xl border p-5 transition-all hover:-translate-y-0.5"
      style={{
        background: isDark
          ? 'linear-gradient(180deg, rgba(255,255,255,0.04) 0%, rgba(255,255,255,0.01) 100%)'
          : 'linear-gradient(180deg, #FFFFFF 0%, rgba(247,245,255,0.6) 100%)',
        borderColor: isDark ? 'rgba(174,82,234,0.18)' : 'rgba(174,82,234,0.20)',
        backdropFilter: 'blur(12px)',
      }}
    >
      <div className="h-10 w-10 rounded-lg bg-[rgba(174,82,234,0.18)] text-[var(--ve-violet)] dark:text-[var(--ve-violet-bright)] flex items-center justify-center border border-[rgba(174,82,234,0.35)]">
        <Icon size={18} />
      </div>
      <div className={`display text-lg mt-4 ${isDark ? 'text-white' : 'text-slate-900'}`}>{title}</div>
      <div className={`text-sm mt-2 leading-relaxed ${isDark ? 'text-white/65' : 'text-slate-600'}`}>{copy}</div>
    </div>
  );
}
